import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { MessageCircle } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { api, money } from "@/lib/api";
import { toast } from "sonner";

const SHOWN_KEY = "kc_cart_reminder_shown";
const IDLE_MS = 45000;

// Exit-intent (desktop) / idle (mobile) popup that captures a WhatsApp number
// for one gentle abandoned-bag reminder. Also opened manually from the cart
// drawer via the "kc:open-cart-reminder" window event.
export const CartReminderPopup = () => {
  const { items, rawSubtotal, count } = useCart();
  const [open, setOpen] = useState(false);
  const [phone, setPhone] = useState("");
  const [consent, setConsent] = useState(false);
  const [sending, setSending] = useState(false);
  const itemsRef = useRef(items);
  itemsRef.current = items;

  useEffect(() => {
    const alreadyShown = () => sessionStorage.getItem(SHOWN_KEY) === "1";
    const reveal = () => {
      if (alreadyShown() || !itemsRef.current.length) return;
      sessionStorage.setItem(SHOWN_KEY, "1");
      setOpen(true);
    };
    // Desktop: pointer leaves the window towards the tab bar
    const onMouseOut = (e) => {
      if (!e.relatedTarget && e.clientY <= 0) reveal();
    };
    // Mobile: long idle with a non-empty bag
    let lastActivity = Date.now();
    const bump = () => { lastActivity = Date.now(); };
    const idleTimer = setInterval(() => {
      const touchOnly = !window.matchMedia("(hover: hover) and (pointer: fine)").matches;
      if (touchOnly && Date.now() - lastActivity > IDLE_MS) reveal();
    }, 5000);
    const manual = () => {
      if (!itemsRef.current.length) { toast.error("Your bag is empty."); return; }
      sessionStorage.setItem(SHOWN_KEY, "1");
      setOpen(true);
    };
    document.addEventListener("mouseout", onMouseOut);
    window.addEventListener("scroll", bump, { passive: true });
    window.addEventListener("touchstart", bump, { passive: true });
    window.addEventListener("click", bump);
    window.addEventListener("kc:open-cart-reminder", manual);
    return () => {
      document.removeEventListener("mouseout", onMouseOut);
      window.removeEventListener("scroll", bump);
      window.removeEventListener("touchstart", bump);
      window.removeEventListener("click", bump);
      window.removeEventListener("kc:open-cart-reminder", manual);
      clearInterval(idleTimer);
    };
  }, []);

  const submit = async () => {
    const digits = phone.replace(/\D/g, "");
    if (digits.length < 10) { toast.error("Enter a valid WhatsApp number."); return; }
    if (!consent) { toast.error("Please tick the box so we can message you."); return; }
    setSending(true);
    try {
      await api.post("/cart-reminders", {
        phone,
        consent,
        items: items.map((i) => ({
          variant_id: i.variant_id, qty: i.qty, title: i.title,
          price: i.price, slug: i.slug, size: i.size, color: i.color,
        })),
      });
      toast.success("Saved! We'll WhatsApp you a link back to your bag.");
      setOpen(false);
    } catch {
      toast.error("Could not save — please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent data-testid="cart-reminder-popup" className="bg-[#FFFFFF] max-w-sm p-0 border border-[#E5E5DF] rounded-none">
        <div className="p-6">
          <div className="w-10 h-10 rounded-full bg-[#25D366]/15 flex items-center justify-center mb-4">
            <MessageCircle size={18} className="text-[#1FA855]" />
          </div>
          <DialogTitle className="font-display text-2xl text-[#1A1A1A]">Still deciding?</DialogTitle>
          <p className="mt-2 text-sm text-[#6E6E69] leading-relaxed">
            Leave your WhatsApp number and we&apos;ll send one gentle reminder with a link
            straight back to your bag ({count} {count === 1 ? "item" : "items"} · {money(rawSubtotal)}).
          </p>
          <div className="mt-5 flex border border-[#E5E5DF] focus-within:border-[#B84A3B] transition-colors">
            <span className="px-3 py-3 text-sm font-mono text-[#6E6E69] bg-[#EFEFEA] border-r border-[#E5E5DF]">+1</span>
            <input
              data-testid="cart-reminder-phone"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="WhatsApp number"
              className="flex-1 px-3 py-3 text-sm focus:outline-none"
            />
          </div>
          <label className="mt-3 flex items-start gap-2 cursor-pointer">
            <input
              data-testid="cart-reminder-consent"
              type="checkbox"
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
              className="mt-0.5 accent-[#B84A3B]"
            />
            <span className="text-[0.7rem] font-mono text-[#6E6E69] leading-relaxed">
              Yes, message me on WhatsApp about my bag. One reminder, no spam.
            </span>
          </label>
          <button
            data-testid="cart-reminder-submit"
            onClick={submit}
            disabled={sending}
            className="mt-5 w-full bg-[#1A1A1A] text-[#FFFFFF] py-3.5 text-[0.68rem] font-mono tracking-[0.2em] uppercase hover:bg-[#B84A3B] transition-colors duration-300 disabled:opacity-60"
          >
            {sending ? "Saving…" : "Remind me on WhatsApp"}
          </button>
          <button
            data-testid="cart-reminder-dismiss"
            onClick={() => setOpen(false)}
            className="mt-3 w-full text-center text-[0.62rem] font-mono tracking-wide text-[#6E6E69] hover:text-[#1A1A1A] transition-colors"
          >
            No thanks, I&apos;ll keep browsing
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
